<template>
  <div class="main-wrapper">
    <div class="pannel-wrapper">
      <div class="annotations-wrapper">
        <div class="annotations-wrapper-header">
          <div
            class="section-header-right-side"
            @click="toggleAnnotations"
          >
            <p class="cluster-title">{{ $t("sidebar.annotations") }}</p>
            <img
              src="@/assets/icons/newArrowUp.svg"
              alt="triangle"
              class="arrow"
              v-show="showAnnotations"
            />
            <img
              src="@/assets/icons/newArrowDown.svg"
              alt="triangle"
              class="arrow"
              v-show="!showAnnotations"
            />
          </div>
        </div>
        <div v-if="showAnnotations" class="annotations-details-box">
          <div
            v-for="(ann, index) in annotations"
            :key="ann.id ?? index"
            class="annotation-inside-wrapper"
          >
            <div
              class="annotation-header"
              :class="{ active: ann.id == selectedAnnotationId }"
              @click="$emit('selectAnnotation', ann)"
            >
              <div class="left-text">
                <p>{{ String(index + 1).padStart(2, '0') }}. {{ getDisplayLabel(ann) }}</p>
              </div>
              <div v-if="ann.severity_level != null" class="severity-badge" :style="{ backgroundColor: severityColor(ann.severity_level) }">
                {{ ann.severity_level }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  annotations: {
    type: Array,
    default: () => [],
  },
  selectedAnnotationId: {
    type: [String, Number],
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  anonymousUser: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["selectAnnotation"]);

const showAnnotations = ref(true);

function toggleAnnotations() {
  if (props.annotations.length) {
    showAnnotations.value = !showAnnotations.value;
  }
}

const DAMAGE_TYPE_KEY_MAP = {
  "Harmful plant growth": "harmful_plant_growth",
  "Defects at roof joints and edges": "defects_at_roof_joints_and_edges",
  "Damages in drainage system": "damages_in_drainage_system",
  "Defects around windows and doors": "defects_around_windows_and_doors",
  "Defects at roof penetrations": "defects_at_roof_penetrations",
  "Defective liquid waterproofing connections": "defective_liquid_waterproofing_connections",
  "Defects at waterproof joints": "defects_at_waterproof_joints",
  "Improper roof slope": "improper_roof_slope",
  "Damage of the material": "damage_of_the_material",
};

function getDisplayLabel(ann) {
  const dt = ann.damage_type;
  if (!dt || dt === "None") return "—";
  const key = DAMAGE_TYPE_KEY_MAP[dt];
  if (key) return t(`annotationPanel.damageTypes.${key}`);
  return dt;
}

const SEVERITY_COLORS = ["#8c8c8c", "#52c41a", "#a0d911", "#faad14", "#fa8c16", "#f5222d"];

function severityColor(level) {
  return SEVERITY_COLORS[level] ?? "#8c8c8c";
}
</script>

<style lang="scss" scoped>
.main-wrapper {
  .pannel-wrapper {
    max-height: 31rem;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;

    .annotations-wrapper {
      .annotations-wrapper-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--color-dark-bg-secondary);
        max-height: 30px;
        display: flex;
        gap: 1rem;
        align-items: center;
        padding: 1rem 0;

        .section-header-right-side {
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: center;
        }

        p {
          font-family: "Roboto";
          font-size: 14px;
          font-style: normal;
          font-weight: bold;
          line-height: 19px;
          color: white;
          letter-spacing: 0.03em;
        }

        img {
          height: 12px;
          width: 18px;
          cursor: pointer;
        }

        .arrow {
          height: 20px;
          width: 20px;
        }
      }

      .annotations-details-box {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 12px;
        margin-top: 6px;

        .annotation-inside-wrapper {
          .annotation-header {
            background: var(--color-dark-bg-primary);
            border: 1px solid var(--color-dark-bg-tertiary);
            display: flex;
            gap: 8px;
            align-items: center;
            padding: 0.75rem 0.5rem;
            max-height: 30px;
            border-radius: 10px;
            justify-content: space-between;
            cursor: pointer;

            &.active {
              border-color: var(--color-gree300);
            }

            &:hover {
              border-color: var(--color-gree300);
              opacity: 0.85;
            }

            .left-text {
              flex: 1;
              width: 0;
              min-width: 0;
              overflow: hidden;

              p {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                cursor: pointer;
                color: white;
                font-family: "Roboto";
                font-size: 14px;
              }
            }

            .severity-badge {
              flex-shrink: 0;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 11px;
              font-weight: 700;
              color: white;
            }
          }
        }
      }
    }
  }
}
</style>
